export type ErrorDomain =
  | 'auth'
  | 'firestore'
  | 'functions'
  | 'storage'
  | 'network'
  | 'payments'
  | 'validation'
  | 'unknown';

export enum ErrorSeverity { 
  Info = 'info', 
  Warn = 'warn', 
  Error = 'error', 
  Fatal = 'fatal' 
}

export enum ErrorCode {
  // Auth
  AUTH_INVALID_CREDENTIALS = 'AUTH/INVALID_CREDENTIALS',
  AUTH_EMAIL_IN_USE = 'AUTH/EMAIL_IN_USE',
  AUTH_USER_DISABLED = 'AUTH/USER_DISABLED',
  AUTH_REQUIRES_RECENT_LOGIN = 'AUTH/REQUIRES_RECENT_LOGIN',
  AUTH_NETWORK = 'AUTH/NETWORK',

  // Firestore
  DB_NOT_FOUND = 'DB/NOT_FOUND',
  DB_PERMISSION_DENIED = 'DB/PERMISSION_DENIED',
  DB_ALREADY_EXISTS = 'DB/ALREADY_EXISTS',
  DB_INDEX_REQUIRED = 'DB/INDEX_REQUIRED',
  DB_UNAVAILABLE = 'DB/UNAVAILABLE',
  TRANSIENT_BACKEND_UNAVAILABLE = 'TRANSIENT_BACKEND_UNAVAILABLE',

  // Functions
  FUNC_UNAVAILABLE = 'FUNC/UNAVAILABLE',
  FUNC_INVALID_ARGUMENT = 'FUNC/INVALID_ARGUMENT',

  // Network
  NET_OFFLINE = 'NET/OFFLINE',
  NET_TIMEOUT = 'NET/TIMEOUT',

  // Payments
  PAY_DECLINED = 'PAY/DECLINED',
  PAY_INVALID = 'PAY/INVALID',

  // Validation
  VALIDATION_FAILED = 'VALIDATION/FAILED',

  // Fallback
  UNKNOWN = 'UNKNOWN',
}

export interface ErrorContext {
  feature?: string;   // e.g. 'cart', 'checkout', 'auth', 'orders'
  action?: string;    // e.g. 'addItem', 'placeOrder', 'signIn'
  location?: string;  // component or file hint
  userId?: string;
  extra?: Record<string, unknown>;
}

export class AppError extends Error {
  readonly code: ErrorCode;
  readonly domain: ErrorDomain;
  readonly severity: ErrorSeverity;
  readonly cause?: unknown;
  readonly ctx?: ErrorContext;
  
  constructor(p: {
    code: ErrorCode; 
    domain: ErrorDomain; 
    severity?: ErrorSeverity;
    message?: string; 
    cause?: unknown; 
    ctx?: ErrorContext;
  }) {
    super(p.message ?? p.code);
    this.name = 'AppError';
    this.code = p.code;
    this.domain = p.domain;
    this.severity = p.severity ?? ErrorSeverity.Error;
    this.cause = p.cause;
    this.ctx = p.ctx;
  }

  /**
   * Check if this error is retryable
   */
  isRetryable(): boolean {
    const nonRetryableCodes: ErrorCode[] = [
      ErrorCode.AUTH_USER_DISABLED,
      ErrorCode.AUTH_EMAIL_IN_USE,
      ErrorCode.DB_PERMISSION_DENIED,
      ErrorCode.PAY_DECLINED,
      ErrorCode.PAY_INVALID,
      ErrorCode.VALIDATION_FAILED
    ];
    return !nonRetryableCodes.includes(this.code);
  }

  /**
   * Get retry delay in seconds
   */
  getRetryDelay(): number {
    switch (this.code) {
      case ErrorCode.AUTH_REQUIRES_RECENT_LOGIN:
        return 300; // 5 minutes
      case ErrorCode.NET_TIMEOUT:
      case ErrorCode.NET_OFFLINE:
        return 10; // 10 seconds
      case ErrorCode.DB_UNAVAILABLE:
      case ErrorCode.FUNC_UNAVAILABLE:
        return 30; // 30 seconds
      default:
        return 0; // No delay
    }
  }

  /**
   * Get user-friendly message
   */
  getUserMessage(): string {
    return this.message;
  }

  /**
   * Get technical details for logging
   */
  getTechnicalDetails(): Record<string, unknown> {
    return {
      code: this.code,
      domain: this.domain,
      severity: this.severity,
      context: this.ctx,
      cause: this.cause,
      stack: this.stack
    };
  }
}
